import { CHROMA } from '@/config/audio';
import { DEFAULTS, SESSION, TRANSITION } from '@/config/training';
import { requireChord } from '@/data/chords';
import { buildTarget } from '@/audio/chord/target';
import { ChordEvaluator } from '@/audio/chord/evaluator';
import type { AudioEngine, EngineFrame } from '@/audio/engine/AudioEngine';
import { Metronome, type BeatInfo } from '@/audio/metronome/Metronome';
import type { ChordAnalysisResult, ChordVerdict } from '@/types/audio';
import type {
  ChordAttempt,
  ChordChangeSettings,
  SessionPhase,
  SessionSummary,
  TransitionSample,
} from '@/types/training';
import { AdaptiveTempo } from '../adaptiveTempo';
import { chordFeedback, type FeedbackItem } from '../feedback';
import { Observable } from '../observable';
import { TransitionTimer } from '../transitionTimer';
import { summarize } from '../summary';

export interface ChordChangeSnapshot {
  phase: SessionPhase;
  /** 1..countInBeats while counting in, 0 otherwise. */
  countInBeat: number;
  chordId: string;
  nextChordId: string;
  /** 0-based beat inside the current chord slot, -1 before the first slot. */
  beatInSlot: number;
  beatsPerChord: number;
  bpm: number;
  tempoHint: 'up' | 'down' | null;
  elapsedSec: number;
  totalSec: number;
  verdict: ChordVerdict | null;
  score: number;
  confidence: number;
  feedback: FeedbackItem[];
  listening: boolean;
  attempts: number;
  correct: number;
  almost: number;
  lastTransitionSec: number | null;
  /**
   * True after several consecutive slots in which the gate heard no guitar.
   * The screen shows a calm hint instead of staying silent: silence from the
   * app while the player is strumming is the most confusing thing it can do.
   */
  hearingNothing: boolean;
  summary: SessionSummary | null;
}

/** Consecutive unheard slots before the hint appears. */
const HEARING_NOTHING_SLOTS = 2;

const IDLE: ChordChangeSnapshot = {
  phase: 'idle',
  countInBeat: 0,
  chordId: '',
  nextChordId: '',
  beatInSlot: -1,
  beatsPerChord: 4,
  bpm: DEFAULTS.bpm,
  tempoHint: null,
  elapsedSec: 0,
  totalSec: 0,
  verdict: null,
  score: 0,
  confidence: 0,
  feedback: [],
  listening: false,
  attempts: 0,
  correct: 0,
  almost: 0,
  lastTransitionSec: null,
  hearingNothing: false,
  summary: null,
};

/**
 * The chord-change trainer.
 *
 * Owns the exercise clock (via the metronome), decides what the player should
 * be doing, and turns the analyser's frame stream into per-slot verdicts and
 * measured transition times. Knows nothing about React or storage: it publishes
 * an immutable snapshot and returns a summary when it finishes.
 */
export class ChordChangeEngine {
  readonly store = new Observable<ChordChangeSnapshot>(IDLE);

  private readonly evaluator: ChordEvaluator;
  private readonly transitionTimer = new TransitionTimer();
  private readonly tempo: AdaptiveTempo;
  private metronome: Metronome | null = null;

  private unsubscribeFrame: (() => void) | null = null;
  private unsubscribeOnset: (() => void) | null = null;
  private unsubscribeBeat: (() => void) | null = null;

  private slotStartBeat: number | null = null;
  private slotStartTime = 0;
  private slotIndex = 0;
  private chordCursor = 0;
  private countInRemaining = DEFAULTS.countInBeats;
  private realign = false;
  private repeatCurrent = false;
  private slotEvaluated = false;
  private slotStabilised = false;
  private practisedSec = 0;
  private lastBeatTime = 0;
  private previousChordId: string | null = null;
  private lastMeasuredTransition: number | null = null;
  private firstSlot = true;
  /** Frames actually evaluated in the current slot. */
  private slotHeardFrames = 0;
  /** Consecutive finalised slots with no guitar frames at all. */
  private unheardSlots = 0;

  private readonly attempts: ChordAttempt[] = [];
  private readonly transitions: TransitionSample[] = [];
  private startedAt = 0;
  private finishing = false;

  constructor(
    private readonly audio: AudioEngine,
    private readonly settings: ChordChangeSettings,
    private readonly onFinished?: (summary: SessionSummary) => void,
  ) {
    this.evaluator = new ChordEvaluator(buildTarget(requireChord(settings.chordIds[0])));
    this.tempo = new AdaptiveTempo(settings.bpm, settings.adaptiveTempo);
  }

  get snapshot(): ChordChangeSnapshot {
    return this.store.getSnapshot();
  }

  /** Start the session. The AudioContext must already be running. */
  start(): void {
    const context = this.audio.audioContext;
    if (!context) throw new Error('AudioEngine has no context — call start() on it first');

    this.startedAt = Date.now();
    this.metronome = new Metronome(context);
    this.metronome.configure({
      bpm: this.settings.bpm,
      beatsPerBar: this.settings.beatsPerChord,
      audible: this.settings.clickEnabled,
    });

    this.audio.setStringAnalysis(false);
    this.audio.setTarget(this.settings.chordIds[0]);
    this.unsubscribeFrame = this.audio.onFrame(this.handleFrame);
    this.unsubscribeOnset = this.audio.onOnset((onset) => this.transitionTimer.registerOnset(onset.time));
    this.unsubscribeBeat = this.metronome.onBeat(this.handleBeat);

    this.countInRemaining = DEFAULTS.countInBeats;
    this.store.set({
      ...IDLE,
      phase: 'countIn',
      chordId: this.settings.chordIds[0],
      nextChordId: this.chordAt(1),
      beatsPerChord: this.settings.beatsPerChord,
      bpm: this.settings.bpm,
      totalSec: this.settings.durationMin * 60,
      countInBeat: 1,
    });

    this.metronome.start();
  }

  pause(): void {
    if (this.snapshot.phase !== 'running' && this.snapshot.phase !== 'countIn') return;
    this.metronome?.stop();
    this.slotStartBeat = null;
    this.transitionTimer.abandon();
    this.store.update({ phase: 'paused', listening: false, beatInSlot: -1 });
  }

  resume(): void {
    if (this.snapshot.phase !== 'paused' || !this.metronome) return;
    this.countInRemaining = DEFAULTS.countInBeats;
    this.evaluator.reset();
    // The interrupted slot never finished, so the player resumes on the same
    // chord instead of finding it silently skipped…
    this.repeatCurrent = true;
    // …and the first slot after an interruption has no honest "from": with
    // three or more chords in rotation, keeping the old previousChordId would
    // record a transition between chords that were never played back to back.
    this.previousChordId = null;
    this.store.update({ phase: 'countIn', countInBeat: 1 });
    this.metronome.start();
  }

  /** Play the same chord again for another slot instead of moving on. */
  repeat(): void {
    this.repeatCurrent = true;
    this.realign = true;
  }

  /** Move to the next chord immediately, realigning the slot to the next beat. */
  skip(): void {
    this.realign = true;
  }

  /** End the session early but keep everything measured so far. */
  finish(): void {
    if (this.finishing) return;
    this.finishing = true;
    this.metronome?.stop();
    this.teardownSubscriptions();
    this.audio.setTarget(null);

    const summary = summarize({
      mode: 'chordChange',
      startedAt: this.startedAt,
      durationSec: Math.round(this.practisedSec),
      chordIds: this.settings.chordIds,
      bpmStart: this.settings.bpm,
      bpmEnd: this.tempo.currentBpm,
      attempts: this.attempts,
      transitions: this.transitions,
      rhythmScore: null,
      clarityScore: null,
    });

    this.store.update({ phase: 'finished', listening: false, summary });
    this.onFinished?.(summary);
  }

  dispose(): void {
    this.metronome?.stop();
    this.teardownSubscriptions();
    this.audio.setTarget(null);
  }

  private teardownSubscriptions(): void {
    this.unsubscribeFrame?.();
    this.unsubscribeOnset?.();
    this.unsubscribeBeat?.();
    this.unsubscribeFrame = null;
    this.unsubscribeOnset = null;
    this.unsubscribeBeat = null;
  }

  // ── clock ─────────────────────────────────────────────────────────────

  private handleBeat = (beat: BeatInfo): void => {
    if (this.finishing) return;
    const previousBeatTime = this.lastBeatTime;
    this.lastBeatTime = beat.time;

    if (this.countInRemaining > 0) {
      // Counted UP: a musician's count-in is "one, two, three, four", not a
      // countdown. It was reading 4-3-2-1, which fights the metronome the
      // player is trying to lock onto.
      const beatNumber = DEFAULTS.countInBeats - this.countInRemaining + 1;
      this.store.update({
        phase: 'countIn',
        countInBeat: beatNumber,
        beatInSlot: -1,
        listening: false,
      });
      this.countInRemaining--;
      if (this.countInRemaining === 0) {
        // The next beat starts the first slot.
        this.slotStartBeat = null;
      }
      return;
    }

    if (previousBeatTime > 0) {
      this.practisedSec += beat.time - previousBeatTime;
    }

    const beatsPerChord = this.settings.beatsPerChord;
    const needNewSlot =
      this.slotStartBeat === null || this.realign || beat.index - this.slotStartBeat >= beatsPerChord;

    if (needNewSlot) {
      if (this.slotStartBeat !== null) this.finalizeSlot();
      this.beginSlot(beat);
    }

    const beatInSlot = beat.index - (this.slotStartBeat ?? beat.index);
    this.store.update({
      phase: 'running',
      countInBeat: 0,
      beatInSlot,
      elapsedSec: this.practisedSec,
      listening: this.isInsideEvaluationWindow(beat.time),
    });

    if (this.practisedSec >= this.settings.durationMin * 60) {
      this.finalizeSlot();
      this.finish();
    }
  };

  private beginSlot(beat: BeatInfo): void {
    if (this.firstSlot) {
      this.firstSlot = false;
    } else if (!this.repeatCurrent) {
      this.chordCursor = (this.chordCursor + 1) % this.settings.chordIds.length;
    }
    this.repeatCurrent = false;
    this.realign = false;

    this.slotStartBeat = beat.index;
    this.slotStartTime = beat.time;
    this.slotIndex++;
    this.slotEvaluated = false;
    this.slotStabilised = false;
    this.slotHeardFrames = 0;

    const chordId = this.settings.chordIds[this.chordCursor];
    this.evaluator.setTarget(buildTarget(requireChord(chordId)));
    this.audio.setTarget(chordId);
    this.transitionTimer.beginTransition(this.previousChordId, beat.time);

    this.store.update({
      chordId,
      nextChordId: this.chordAt(this.chordCursor + 1),
      verdict: null,
      score: 0,
      confidence: 0,
      feedback: [],
      beatInSlot: 0,
    });
  }

  private finalizeSlot(): void {
    if (this.slotEvaluated) return;
    // A slot the analyser never got a frame for is not an attempt at anything.
    if (this.slotHeardFrames === 0) {
      this.slotEvaluated = true;
      this.previousChordId = this.settings.chordIds[this.chordCursor];
      this.lastMeasuredTransition = null;
      this.evaluator.reset();
      this.unheardSlots++;
      if (this.unheardSlots >= HEARING_NOTHING_SLOTS && !this.snapshot.hearingNothing) {
        this.store.update({ hearingNothing: true });
      }
      return;
    }
    this.slotEvaluated = true;
    if (this.unheardSlots > 0) {
      this.unheardSlots = 0;
      if (this.snapshot.hearingNothing) this.store.update({ hearingNothing: false });
    }

    const chordId = this.settings.chordIds[this.chordCursor];
    const result = this.evaluator.latest;
    const verdict: ChordVerdict = result?.status ?? 'uncertain';

    const attempt: ChordAttempt = {
      chordId,
      verdict,
      score: result?.targetScore ?? 0,
      confidence: result?.confidence ?? 0,
      bpm: this.tempo.currentBpm,
      transitionSec: this.lastMeasuredTransition,
      ...(this.previousChordId ? { previousChordId: this.previousChordId } : {}),
    };
    this.attempts.push(attempt);

    // Only conclusive slots become transition statistics. An `uncertain`
    // verdict means the analyser could not hear the room — recording that as a
    // failed change would let a noisy kitchen quietly destroy a player's
    // success rate, and then send the recommendation engine after a transition
    // they can actually play.
    if (this.previousChordId && this.previousChordId !== chordId && verdict !== 'uncertain') {
      this.transitions.push({
        key: `${this.previousChordId}>${chordId}`,
        from: this.previousChordId,
        to: chordId,
        bpm: this.tempo.currentBpm,
        success: verdict === 'correct' || verdict === 'almost',
        seconds: this.lastMeasuredTransition,
        at: Date.now(),
      });
    }

    if (!this.slotStabilised) this.transitionTimer.abandon();

    const decision = this.tempo.record(verdict, this.lastBeatTime);
    if (decision.changed && this.metronome) {
      this.metronome.changeTempo(decision.bpm);
      this.store.update({
        bpm: decision.bpm,
        tempoHint: decision.direction === 'hold' ? null : decision.direction,
      });
    }

    const snapshot = this.snapshot;
    this.store.update({
      attempts: snapshot.attempts + 1,
      correct: snapshot.correct + (verdict === 'correct' ? 1 : 0),
      almost: snapshot.almost + (verdict === 'almost' ? 1 : 0),
    });

    this.previousChordId = chordId;
    this.lastMeasuredTransition = null;
    this.evaluator.reset();
  }

  // ── analysis ──────────────────────────────────────────────────────────

  private handleFrame = (frame: EngineFrame): void => {
    if (this.snapshot.phase !== 'running') return;
    if (!this.isInsideEvaluationWindow(frame.time)) return;
    // Only frames the gate attributes to the guitar are scored. `non-guitar`
    // (speech, taps, room noise) and `uncertain` alike are not part of the
    // attempt: they are not scored, do not count as "heard", and a slot
    // containing only them produces no attempt at all. The distinction
    // matters — an `uncertain` frame fed to the evaluator would be judged by
    // the evaluator's own confidence model, which knows nothing about the
    // gate, and loud speech is confident-looking by that model. Measured: 19
    // "Пока не то" verdicts over six seconds of continuous speech leaked
    // through exactly this path before the check covered `uncertain`.
    // (A frame with no source at all comes only from unit fixtures.)
    if (frame.source && frame.source.kind !== 'guitar') return;

    this.slotHeardFrames++;
    const result: ChordAnalysisResult = this.evaluator.push(frame, frame.bass);

    if (!this.slotStabilised && this.evaluator.consecutiveStableFrames >= TRANSITION.stableFrames) {
      this.slotStabilised = true;
      const seconds = this.transitionTimer.confirmStable(this.snapshot.chordId, frame.time);
      if (seconds !== null) {
        this.lastMeasuredTransition = seconds;
        this.store.update({ lastTransitionSec: seconds });
      }
    }

    this.store.update({
      listening: true,
      verdict: result.status,
      score: result.targetScore,
      confidence: result.confidence,
      feedback: chordFeedback(result, requireChord(this.snapshot.chordId).name),
    });
  };

  /**
   * The chroma window is 682 ms long, so a frame taken right after a chord
   * change still contains the *previous* chord. Evaluation therefore starts
   * once the window has moved past the boundary — never earlier, whatever the
   * configured fraction says.
   */
  private isInsideEvaluationWindow(time: number): boolean {
    if (this.slotStartBeat === null) return false;
    const slotDuration = (this.settings.beatsPerChord * 60) / this.tempo.currentBpm;
    const decimatedRate =
      (this.audio.getSnapshot().contextSampleRate || 48000) / CHROMA.decimation;
    const chromaWindowSec = CHROMA.fftSize / decimatedRate;

    const to = this.slotStartTime + slotDuration * SESSION.evaluationEndFraction;
    let from = this.slotStartTime + Math.max(slotDuration * SESSION.evaluationStartFraction, chromaWindowSec);
    // Very short slots (2 beats at a fast tempo) are shorter than the analysis
    // window itself. Rather than evaluating nothing, we take the last sliver of
    // the slot and let the stability term drag confidence down — which is the
    // honest outcome: at that tempo the analyser genuinely cannot be sure.
    if (from > to - SESSION.minEvaluationSec) from = to - SESSION.minEvaluationSec;

    return time >= from && time <= to;
  }

  private chordAt(index: number): string {
    const ids = this.settings.chordIds;
    return ids[index % ids.length];
  }
}
